import React from 'react';
import { colors, spacing, typography, borderRadius, transitions } from '../../theme/tokens';

/**
 * Select Component
 *
 * A dropdown select component with consistent styling.
 *
 * @param {string} label - Label text above the select
 * @param {Array} options - Array of option objects: [{ value: 'val1', label: 'Label 1' }]
 * @param {string} value - Currently selected value
 * @param {function} onChange - Callback when selection changes: (event) => void
 * @param {string} error - Error message to display
 * @param {string} helperText - Helper text below the select
 * @param {boolean} fullWidth - Whether select should fill full width
 * @param {boolean} disabled - Whether select is disabled
 */
const Select = ({
  label,
  options = [],
  value,
  onChange,
  error,
  helperText,
  fullWidth = false,
  disabled = false,
  placeholder = 'Select an option',
  className = '',
  ...props
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.xs,
        width: fullWidth ? '100%' : 'auto',
      }}
      className={className}
    >
      {label && (
        <label
          style={{
            fontSize: typography.fontSize.xs,
            fontWeight: typography.fontWeight.medium,
            color: error ? colors.status.error : colors.text.secondary,
          }}
        >
          {label}
        </label>
      )}

      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        {...props}
        style={{
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: typography.fontSize.sm,
          background: disabled ? colors.bg.tertiary : colors.bg.primary,
          border: `1px solid ${error ? colors.border.error : colors.border.default}`,
          borderRadius: borderRadius.md,
          color: disabled ? colors.text.disabled : colors.text.primary,
          outline: 'none',
          transition: transitions.normal,
          width: fullWidth ? '100%' : 'auto',
          cursor: disabled ? 'not-allowed' : 'pointer',
          ...props.style,
        }}
        onFocus={(e) => {
          if (!error && !disabled) {
            e.target.style.borderColor = colors.border.focus;
          }
        }}
        onBlur={(e) => {
          if (!error && !disabled) {
            e.target.style.borderColor = colors.border.default;
          }
        }}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {(error || helperText) && (
        <span
          style={{
            fontSize: typography.fontSize.xs,
            color: error ? colors.status.error : colors.text.tertiary,
          }}
        >
          {error || helperText}
        </span>
      )}
    </div>
  );
};

export default Select;
